import { useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { usePublicTrackingLinkStore } from "../../../store/public/publicTrackingLinkStore";
import { AccessCodeModal } from "./components/AccessCodeModal";
import { LinkStatusScreen } from "./components/LinkStatusScreen";
import { PublicTrackingMap } from "./components/PublicTrackingMap";

export default function PublicTrackingLinkPage() {
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");

  const { linkData, status, errorMessage, loading, startRealTimeUpdates, stopRealTimeUpdates, reset } =
    usePublicTrackingLinkStore();

  // Start real-time updates on mount
  useEffect(() => {
    if (!hash) return;

    // Start real-time updates
    startRealTimeUpdates(hash);

    return () => {
      stopRealTimeUpdates();
      reset();
    };
  }, [hash, startRealTimeUpdates, stopRealTimeUpdates, reset]);

  // Handle access code submission
  const handleAccessCodeSubmit = useCallback(
    (code: string) => {
      if (hash) {
        // Start real-time updates with access code
        startRealTimeUpdates(hash, code);
      }
    },
    [hash, startRealTimeUpdates],
  );

  // Show loading screen
  if (!hash) {
    return (
      <LinkStatusScreen
        status="not_found"
        errorMessage="No se proporcionó un hash de link válido"
      />
    );
  }

  // Show status screens for non-active states
  if (status === "loading") {
    return <LinkStatusScreen status="loading" />;
  }

  if (status === "access_required" || status === "invalid_code") {
    return (
      <AccessCodeModal
        onSubmit={handleAccessCodeSubmit}
        loading={loading}
        error={status === "invalid_code" ? errorMessage : null}
        linkName={linkData?.name}
      />
    );
  }

  if (
    status === "not_found" ||
    status === "expired" ||
    status === "inactive" ||
    status === "pending"
  ) {
    return <LinkStatusScreen status={status} />;
  }

  if (status === "error") {
    return <LinkStatusScreen status={status} errorMessage={errorMessage} />;
  }

  // Show map when active
  if (status === "active" && linkData) {
    return (
      <PublicTrackingMap 
        vehicles={linkData.units} 
        activeUsers={linkData.activeUsers} 
        linkName={linkData.name} 
      />
    );
  }

  return <LinkStatusScreen status="loading" />;
}
